import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Brain, CheckCircle2, AlertTriangle, TrendingUp,
  MessageSquareQuote, Activity, ChevronDown, ChevronUp, Clock,
  FileText, Tag as TagIcon, HelpCircle, Phone, Zap
} from 'lucide-react';
import { FiPlay, FiPause, FiVolume2 } from 'react-icons/fi';
import Forward5Icon from '@mui/icons-material/Forward5';
import Replay5Icon from '@mui/icons-material/Replay5';
import { Box } from '@mui/material';
import WaveSurfer from 'wavesurfer.js';
import { apiRequest } from '../../api/apiHelper';
import '../home/MeetingDetails.css';

// ─── helpers ────────────────────────────────────────────────────────────────

function parseCoachingData(raw) {
  if (!raw) return null;
  try {
    const cleaned = raw.replace(/^```json\s*/i, '').replace(/```\s*$/, '').trim();
    return JSON.parse(cleaned);
  } catch { return null; }
}

function timeToSeconds(t = '00:00:00') {
  const parts = t.split(':').map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return 0;
}

// ─── Tag — clickable when onSeek provided ────────────────────────────────────

function Tag({ time, onSeek }) {
  if (!time) return null;
  // Extract first timestamp from ranges like "00:00:01" or "00:00:01 – 00:00:35"
  const firstTime = time.split(/[–\-]/)[0].trim();
  const secs = timeToSeconds(firstTime);
  const clickable = !!onSeek && secs > 0;
  return (
    <span
      onClick={clickable ? (e) => { e.stopPropagation(); onSeek(secs); } : undefined}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '3px',
        background: clickable ? '#eff6ff' : '#f3f4f6',
        color: clickable ? '#2563eb' : '#6b7280',
        borderRadius: '5px', padding: '1px 6px', fontSize: '10px', fontWeight: 500,
        marginLeft: '6px', flexShrink: 0,
        cursor: clickable ? 'pointer' : 'default',
        border: clickable ? '1px solid #bfdbfe' : 'none',
        transition: 'background 0.15s'
      }}>
      <Clock size={9} /> {time}
    </span>
  );
}

// ─── WaveSurfer Visualizer (same as MeetingDetails) ─────────────────────────

const WaveformVisualizer = ({ audioUrl, isPlaying, onPlayPause, seekTime }) => {
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);

  useEffect(() => {
    if (!waveformRef.current || !audioUrl) return;
    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: '#d4d4d8',
      progressColor: '#7c3aed',
      cursorColor: '#7c3aed',
      barWidth: 3, barRadius: 3, barGap: 2,
      responsive: true, height: 80, normalize: true,
      backend: 'WebAudio', backgroundColor: 'transparent',
    });
    if (audioUrl) {
      wavesurfer.load(audioUrl).catch(err => {
        if (err.name !== 'AbortError') console.warn('WaveSurfer load error:', err);
      });
    }
    wavesurferRef.current = wavesurfer;
    wavesurfer.on('ready', () => { if (isPlaying) wavesurfer.play(); });
    wavesurfer.on('finish', () => { if (onPlayPause) onPlayPause(false); });
    return () => {
      try { if (wavesurfer) wavesurfer.destroy(); } catch (e) { if (e.name !== 'AbortError') console.error(e); }
    };
  }, [audioUrl]);

  // Sync play/pause
  useEffect(() => {
    if (wavesurferRef.current) {
      isPlaying ? wavesurferRef.current.play() : wavesurferRef.current.pause();
    }
  }, [isPlaying]);

  // Sync seek position — when controls bar seekbar is used, update WaveSurfer progress
  useEffect(() => {
    const ws = wavesurferRef.current;
    if (!ws || seekTime == null) return;
    try {
      const dur = ws.getDuration();
      if (dur > 0) {
        const progress = Math.max(0, Math.min(1, seekTime / dur));
        ws.seekTo(progress);
      }
    } catch (e) { /* ignore if not ready */ }
  }, [seekTime]);

  return (
    <Box ref={waveformRef}
      sx={{ width: '90%', height: '80px', margin: '0 auto', cursor: 'default', '& wave': { overflow: 'visible !important' } }}
    />
  );
};

// ─── Media Player (same layout as MeetingDetails VideoPlayer) ────────────────

function MediaPlayer({ audioUrl, videoUrl, onTimeUpdate, seekRef }) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [seekTime, setSeekTime] = useState(null);

  const fmt = (t) => {
    if (!t || isNaN(t)) return '0:00';
    const m = Math.floor(t / 60); const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const onTime = () => {
      setCurrentTime(el.currentTime);
      setDuration(el.duration || 0);
      if (onTimeUpdate) onTimeUpdate(el.currentTime, el.duration || 0);
    };
    const onMeta = () => setDuration(el.duration || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnd = () => setIsPlaying(false);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnd);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnd);
    };
  }, [videoUrl, audioUrl]);

  const togglePlay = async () => {
    if (!videoRef.current) return;
    try { isPlaying ? videoRef.current.pause() : await videoRef.current.play(); }
    catch (e) { setIsPlaying(false); }
  };

  const skip = (s) => {
    if (!videoRef.current) return;
    const newTime = Math.max(0, Math.min(duration, currentTime + s));
    videoRef.current.currentTime = newTime;
    setSeekTime(newTime);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    const next = !isMuted;
    videoRef.current.muted = next;
    setIsMuted(next);
  };

  const changeRate = () => {
    const rates = [0.5, 1, 1.25, 1.5, 2];
    const next = rates[(rates.indexOf(playbackRate) + 1) % rates.length];
    if (videoRef.current) videoRef.current.playbackRate = next;
    setPlaybackRate(next);
  };

  const handleSeek = (e) => {
    if (!videoRef.current || !duration) return;
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const newTime = pct * duration;
    videoRef.current.currentTime = newTime;
    setSeekTime(newTime);
  };

  // Expose seek by seconds for timestamp clicks
  useEffect(() => {
    if (seekRef) {
      seekRef.current = (secs) => {
        if (!videoRef.current) return;
        videoRef.current.currentTime = secs;
        setSeekTime(secs);
        if (!isPlaying) videoRef.current.play().catch(() => {});
      };
    }
  });

  if (!audioUrl && !videoUrl) return null;

  const pct = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div style={{ background: '#000', borderRadius: '0 0 12px 12px', overflow: 'hidden', position: 'relative' }}>
      {/* Hidden audio / visible video */}
      {videoUrl ? (
        <video ref={videoRef} src={videoUrl} style={{ width: '100%', display: 'block', maxHeight: '160px', objectFit: 'contain' }} />
      ) : (
        <>
          <audio ref={videoRef} src={audioUrl} style={{ display: 'none' }} />
          {/* WaveSurfer waveform area — click disabled, seek via controls bar only */}
          <div style={{ background: 'radial-gradient(circle at center, #1a1a2e 0%, #000 100%)', padding: '16px 0 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100px', pointerEvents: 'none' }}>
            <WaveformVisualizer audioUrl={audioUrl} isPlaying={isPlaying} seekTime={seekTime}
              onPlayPause={(p) => { p ? videoRef.current?.play() : videoRef.current?.pause(); }} />
          </div>
        </>
      )}

      {/* Center play button overlay — always clickable */}
      <div onClick={togglePlay} style={{
        position: 'absolute', top: videoUrl ? '40%' : '38%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '48px', height: '48px', borderRadius: '50%',
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', border: '2px solid rgba(255,255,255,0.3)',
        transition: 'opacity 0.2s', opacity: isPlaying ? 0 : 1,
        pointerEvents: isPlaying ? 'none' : 'auto', zIndex: 2
      }}>
        <FiPlay size={20} color="white" />
      </div>

      {/* Always-visible controls bar */}
      <div style={{
        background: 'rgba(0,0,0,0.85)', padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: '6px'
      }}>
        {/* Play/Pause */}
        <button onClick={togglePlay} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', padding: '2px' }}>
          {isPlaying ? <FiPause size={18} color="#fff" /> : <FiPlay size={18} color="#fff" />}
        </button>

        {/* Skip back */}
        <button onClick={() => skip(-10)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', padding: '2px' }}>
          <Replay5Icon sx={{ fontSize: 18, color: '#fff' }} />
        </button>

        {/* Skip forward */}
        <button onClick={() => skip(10)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', padding: '2px' }}>
          <Forward5Icon sx={{ fontSize: 18, color: '#fff' }} />
        </button>

        {/* Mute */}
        <button onClick={toggleMute} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', padding: '2px' }}>
          <FiVolume2 size={16} color={isMuted ? '#6b7280' : '#fff'} />
        </button>

        {/* Time */}
        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap', minWidth: '72px' }}>
          {fmt(currentTime)} / {fmt(duration)}
        </span>

        {/* Seekbar — clicks always land on the outer bar */}
        <div onClick={handleSeek} style={{
          flex: 1, height: '4px', background: 'rgba(255,255,255,0.2)', borderRadius: '99px',
          cursor: 'pointer', position: 'relative', overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${pct}%`, background: '#7c3aed', borderRadius: '99px', pointerEvents: 'none' }} />
        </div>

        {/* Speed */}
        <button onClick={changeRate} style={{
          background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: '4px', cursor: 'pointer', color: '#fff', fontSize: '11px',
          fontWeight: 600, padding: '2px 7px', whiteSpace: 'nowrap'
        }}>
          {playbackRate}x
        </button>
      </div>
    </div>
  );
}

// ─── Call Info Card ───────────────────────────────────────────────────────────

function CallInfoCard({ info }) {
  if (!info) return null;
  const { meetingName, startTime, duration, meetingType, callScore } = info;

  const fmtDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ', ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const scoreColor = callScore >= 8 ? '#16a34a' : callScore >= 6 ? '#f59e0b' : '#ef4444';
  const scoreLabel = callScore >= 8 ? 'Excellent' : callScore >= 6 ? 'Good' : 'Needs Work';
  const radius = 26, circ = 2 * Math.PI * radius;
  const dash = Math.min(Math.max((callScore || 0) / 10, 0), 1) * circ;
  const dateStr = fmtDate(startTime);

  return (
    <div style={{
      background: '#fff', borderRadius: '12px', overflow: 'hidden',
      boxShadow: '0 1px 6px rgba(0,0,0,0.07)', border: '1px solid #f0f0f0', marginBottom: '14px'
    }}>
      <div style={{ height: '3px', background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)' }} />
      <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
        <div style={{
          width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
          background: 'linear-gradient(135deg, #eff6ff, #f5f3ff)', border: '1px solid #e0e7ff',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <Phone size={16} color="#3b82f6" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827', marginBottom: '4px' }}>
            {meetingName || 'Call'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
            {dateStr && <span style={{ fontSize: '12px', color: '#6b7280' }}>{dateStr}</span>}
            {duration && <><span style={{ color: '#d1d5db' }}>•</span><span style={{ fontSize: '12px', color: '#6b7280' }}>{duration}</span></>}
            {meetingType && <><span style={{ color: '#d1d5db' }}>•</span>
              <span style={{ fontSize: '10px', fontWeight: 600, color: '#3b82f6', background: '#eff6ff', borderRadius: '4px', padding: '1px 6px', border: '1px solid #bfdbfe' }}>{meetingType}</span>
            </>}
          </div>
        </div>
        {callScore != null && (
          <div style={{ flexShrink: 0, textAlign: 'center' }}>
            <div style={{ position: 'relative', width: '64px', height: '64px', margin: '0 auto 3px' }}>
              <svg width="64" height="64" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="32" cy="32" r={radius} fill="none" stroke="#f3f4f6" strokeWidth="5" />
                <circle cx="32" cy="32" r={radius} fill="none" stroke={scoreColor} strokeWidth="5"
                  strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
              </svg>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '14px', fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{callScore}</span>
                <span style={{ fontSize: '8px', color: '#9ca3af' }}>/10</span>
              </div>
            </div>
            <div style={{ fontSize: '10px', fontWeight: 600, color: scoreColor }}>{scoreLabel}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Transcript Panel ─────────────────────────────────────────────────────────

const SPEAKER_COLORS = ['#1976d2', '#43cea2', '#f44336', '#ff9800', '#9c27b0', '#00bcd4'];

function TranscriptPanel({ transcript, activeIndex, transcriptRef }) {
  if (!transcript?.length) return (
    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af' }}>
      <FileText size={32} color="#d1d5db" style={{ marginBottom: '8px' }} />
      <p style={{ fontSize: '13px', margin: 0 }}>No transcript available</p>
    </div>
  );

  const speakerColorMap = {};
  let colorIdx = 0;
  transcript.forEach(({ speaker }) => {
    if (speaker && !speakerColorMap[speaker]) {
      speakerColorMap[speaker] = SPEAKER_COLORS[colorIdx++ % SPEAKER_COLORS.length];
    }
  });

  const speakerLabel = (s) => s === 'A' ? 'Agent' : s === 'B' ? 'Customer' : `Speaker ${s}`;

  const fmtSecs = (s) => {
    if (s == null || isNaN(s)) return null;
    const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div ref={transcriptRef} style={{ display: 'flex', flexDirection: 'column' }}>
      {transcript.map((line, i) => {
        const label = speakerLabel(line.speaker);
        const color = speakerColorMap[line.speaker] || '#6b7280';
        const initial = label.charAt(0).toUpperCase();
        const isActive = i === activeIndex;
        const timeLabel = fmtSecs(line.start_time ?? line.start);
        return (
          <div key={i} id={`t-line-${i}`} style={{
            display: 'flex', gap: '12px', alignItems: 'flex-start',
            padding: '12px 8px', borderRadius: '8px',
            borderBottom: i < transcript.length - 1 ? '1px solid #f3f4f6' : 'none',
            background: isActive ? '#eff6ff' : 'transparent',
            transition: 'background 0.3s ease'
          }}>
            <div style={{
              width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
              background: color, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: '13px', fontWeight: 700,
              border: isActive ? `2px solid ${color}` : '2px solid transparent',
              boxShadow: isActive ? `0 0 0 2px ${color}44` : 'none',
              transition: 'box-shadow 0.3s'
            }}>
              {initial}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#111827' }}>{label}</span>
                {timeLabel && (
                  <span style={{
                    fontSize: '10px', color: '#9ca3af', fontWeight: 500,
                    background: '#f3f4f6', borderRadius: '4px', padding: '1px 5px'
                  }}>
                    {timeLabel}
                  </span>
                )}
              </div>
              <div style={{
                fontSize: '13px', lineHeight: '1.55',
                color: isActive ? '#1e40af' : '#4b5563',
                fontWeight: isActive ? 500 : 400,
                transition: 'color 0.3s'
              }}>
                {line.text}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Coaching Tab Sections ────────────────────────────────────────────────────

function FeedbackItem({ icon, color, bg, title, reason, time, onSeek, softIcon }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ background: bg, borderRadius: '9px', padding: '10px 12px', marginBottom: '8px', border: `1px solid ${color}22` }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: reason ? 'pointer' : 'default' }}
        onClick={() => reason && setOpen(o => !o)}>
        {softIcon
          ? <span style={{ flexShrink: 0, marginTop: '2px', width: '16px', height: '16px', borderRadius: '50%', background: color + '22', border: `1.5px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: color, display: 'block' }} />
            </span>
          : <span style={{ fontSize: '14px', flexShrink: 0, marginTop: '1px' }}>{icon}</span>
        }
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>{title}</span>
          <Tag time={time} onSeek={onSeek} />
        </div>
        {reason && <span style={{ color: '#9ca3af', flexShrink: 0 }}>{open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}</span>}
      </div>
      {open && reason && <p style={{ margin: '6px 0 0 22px', fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>{reason}</p>}
    </div>
  );
}

function ScriptCard({ instead_of, try: tryText, time, onSeek }) {
  return (
    <div style={{ borderRadius: '10px', overflow: 'hidden', marginBottom: '12px', border: '1px solid #e5e7eb' }}>
      <div style={{ background: '#fef2f2', padding: '10px 14px', borderBottom: '1px solid #fecaca' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '5px' }}>
          <span style={{ fontSize: '10px', fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Instead of</span>
          <Tag time={time} onSeek={onSeek} />
        </div>
        <p style={{ margin: 0, fontSize: '12.5px', color: '#7f1d1d', fontStyle: 'italic', lineHeight: '1.5' }}>"{instead_of}"</p>
      </div>
      <div style={{ background: '#f0fdf4', padding: '10px 14px' }}>
        <span style={{ fontSize: '10px', fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '5px' }}>Try this</span>
        <p style={{ margin: 0, fontSize: '12.5px', color: '#14532d', lineHeight: '1.5' }}>"{tryText}"</p>
      </div>
    </div>
  );
}

const SENTIMENT_COLOR = { positive: '#16a34a', neutral: '#f59e0b', negative: '#ef4444', bad: '#dc2626' };
const SENTIMENT_BG = { positive: '#f0fdf4', neutral: '#fffbeb', negative: '#fef2f2', bad: '#fef2f2' };

function SentimentTimeline({ timeline, onSeek }) {
  if (!timeline) return null;
  const points = Object.entries(timeline).map(([key, val]) => ({
    label: key.charAt(0).toUpperCase() + key.slice(1),
    sentiment: val.sentiment, reason: val.reason, time: val.mentioned_time
  }));
  const totalSecs = Math.max(...points.map(p => timeToSeconds(p.time)), 1);

  return (
    <div>
      <div style={{ position: 'relative', height: '48px', marginBottom: '20px', marginTop: '4px' }}>
        <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '5px', background: '#f3f4f6', borderRadius: '99px', transform: 'translateY(-50%)' }} />
        <div style={{ position: 'absolute', top: '50%', left: 0, width: `${(timeToSeconds(points[points.length - 1]?.time) / totalSecs) * 100}%`, height: '5px', borderRadius: '99px', transform: 'translateY(-50%)', background: 'linear-gradient(90deg, #3b82f6, #f59e0b, #ef4444)' }} />
        {points.map((p, i) => {
          const pct = totalSecs > 0 ? (timeToSeconds(p.time) / totalSecs) * 100 : (i / (points.length - 1)) * 100;
          const color = SENTIMENT_COLOR[(p.sentiment || '').toLowerCase()] || '#6b7280';
          return <div key={i} style={{ position: 'absolute', top: '50%', left: `${pct}%`, transform: 'translate(-50%, -50%)', width: '16px', height: '16px', borderRadius: '50%', background: color, border: '3px solid #fff', boxShadow: `0 0 0 2px ${color}44` }} />;
        })}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${points.length}, 1fr)`, gap: '10px' }}>
        {points.map((p, i) => {
          const key = (p.sentiment || '').toLowerCase();
          const color = SENTIMENT_COLOR[key] || '#6b7280';
          const bg = SENTIMENT_BG[key] || '#f9fafb';
          return (
            <div key={i} style={{ background: bg, borderRadius: '9px', padding: '10px', border: `1px solid ${color}33`, textAlign: 'center' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>{p.label}</div>
              <div style={{ display: 'inline-block', padding: '1px 8px', borderRadius: '20px', background: color + '22', color, fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>{p.sentiment}</div>
              <Tag time={p.time} onSeek={onSeek} />
              <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#6b7280', lineHeight: '1.5' }}>{p.reason}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Q&A Card (rich analytics format) ────────────────────────────────────────

const CATEGORY_COLOR = {
  features: '#3b82f6', pricing: '#8b5cf6', earnings: '#16a34a',
  usage: '#06b6d4', requirements: '#f59e0b', general: '#6b7280'
};

function QnaCard({ item, onSeek }) {
  const [open, setOpen] = useState(false);
  const color = CATEGORY_COLOR[(item.category || '').toLowerCase()] || '#6b7280';
  const score = item.importance_score;
  const scoreColor = score >= 8 ? '#16a34a' : score >= 6 ? '#f59e0b' : '#ef4444';

  return (
    <div style={{ borderRadius: '10px', overflow: 'hidden', marginBottom: '12px', border: '1px solid #e5e7eb', background: '#fff' }}>
      {/* Question header */}
      <div style={{ padding: '12px 14px', background: '#f8faff', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <span style={{ fontSize: '9px', fontWeight: 700, color: '#2563eb', background: '#dbeafe', borderRadius: '3px', padding: '2px 6px', flexShrink: 0, marginTop: '3px' }}>Q</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
              <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#1e40af', lineHeight: '1.55', flex: 1 }}>{item.question}</p>
              {item.mentioned_time && <Tag time={item.mentioned_time} onSeek={onSeek} />}
            </div>
          </div>
        </div>
      </div>

      {/* Agent's actual answer */}
      {item.agent_answer && (
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <span style={{ fontSize: '9px', fontWeight: 700, color: '#6b7280', background: '#f3f4f6', borderRadius: '3px', padding: '2px 6px', flexShrink: 0, marginTop: '2px' }}>Agent</span>
            <p style={{ margin: 0, fontSize: '12px', color: '#4b5563', lineHeight: '1.55', fontStyle: 'italic' }}>"{item.agent_answer}"</p>
          </div>
        </div>
      )}

      {/* Issue + Suggested answer (expandable) */}
      {(item.issue || item.suggested_answer) && (
        <div>
          <button
            onClick={() => setOpen(o => !o)}
            style={{
              width: '100%', background: 'none', border: 'none', cursor: 'pointer',
              padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '6px',
              fontSize: '11px', fontWeight: 600, color: '#7c3aed', textAlign: 'left'
            }}
          >
            <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: '#f5f3ff', border: '1px solid #c4b5fd', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: '8px', color: '#7c3aed' }}>{open ? '▲' : '▼'}</span>
            </span>
            {open ? 'Hide coaching tip' : 'View coaching tip & suggested answer'}
          </button>
          {open && (
            <div style={{ padding: '0 14px 12px' }}>
              {item.issue && (
                <div style={{ background: '#fffbeb', borderRadius: '7px', padding: '8px 10px', marginBottom: '8px', border: '1px solid #fde68a' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '3px' }}>Issue</div>
                  <p style={{ margin: 0, fontSize: '12px', color: '#78350f', lineHeight: '1.5' }}>{item.issue}</p>
                </div>
              )}
              {item.suggested_answer && (
                <div style={{ background: '#f0fdf4', borderRadius: '7px', padding: '8px 10px', border: '1px solid #bbf7d0' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#14532d', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '3px' }}>Suggested Answer</div>
                  <p style={{ margin: 0, fontSize: '12px', color: '#166534', lineHeight: '1.55' }}>{item.suggested_answer}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Skeleton({ h = 14, w = '100%', mb = 8 }) {
  return <div style={{ height: h, width: w, borderRadius: 6, background: 'linear-gradient(90deg, #f3f4f6 25%, #e9eaec 50%, #f3f4f6 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', marginBottom: mb }} />;
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function Coaching() {
  const navigate = useNavigate();
  const { meetingId } = useParams();
  const location = useLocation();
  const dealId = new URLSearchParams(location.search).get('dealId');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [qna, setQna] = useState([]);
  const [callInfo, setCallInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('feedback');
  const [activeTranscriptIndex, setActiveTranscriptIndex] = useState(-1);
  const transcriptScrollRef = useRef(null);
  const transcriptContainerRef = useRef(null);
  const transcriptRef = useRef([]); // always-current ref to avoid stale closures
  const activeTranscriptIndexRef = useRef(-1); // track last index to avoid redundant scrolls
  // Ref to expose seek function from MediaPlayer up to parent
  const mediaSeekRef = useRef(null);

  useEffect(() => {
    if (!meetingId) return;
    setLoading(true);
    setError(null);
    apiRequest('get', `/insights/coaching/${meetingId}`)
      .then(res => {
        const raw = res?.data?.coachingInsights || res?.coachingInsights;
        setData(parseCoachingData(raw));
      })
      .catch(err => setError(err?.message || 'Failed to load coaching insights'))
      .finally(() => setLoading(false));
  }, [meetingId]);

  useEffect(() => {
    if (!meetingId) return;
    apiRequest('get', `/trans/transcript_v1/${meetingId}`)
      .then(res => {
        if (res?.audioUrl) setAudioUrl(res.audioUrl);
        if (res?.videoUrl) setVideoUrl(res.videoUrl);
        if (Array.isArray(res?.transcript)) {
          setTranscript(res.transcript);
          transcriptRef.current = res.transcript;
        }
      })
      .catch(() => {});
  }, [meetingId]);

  useEffect(() => {
    if (!meetingId) return;
    apiRequest('get', `/trans/generate-sales-coach/${meetingId}`)
      .then(res => {
        const phase4Raw = res?.phaseResponses?.phase4;
        if (!phase4Raw) return;
        try {
          const cleaned = phase4Raw.replace(/^```[a-z]*\s*/i, '').replace(/```\s*$/, '').trim();
          const parsed = JSON.parse(cleaned);
          if (Array.isArray(parsed?.keywords_and_entities?.keywords)) setKeywords(parsed.keywords_and_entities.keywords);
        } catch { }
      })
      .catch(() => {});
  }, [meetingId]);

  // Fetch rich Q&A from top-questions analytics API
  useEffect(() => {
    if (!meetingId) return;
    apiRequest('get', `/analytics/top-questions?meetingId=${meetingId}`)
      .then(res => {
        const arr = res?.data;
        if (!Array.isArray(arr)) return;
        const questions = arr.flatMap(item =>
          Array.isArray(item.key_questions_analysis) ? item.key_questions_analysis : []
        );
        if (questions.length > 0) setQna(questions);
      })
      .catch(() => {});
  }, [meetingId]);

  useEffect(() => {
    if (!dealId || !meetingId) return;
    apiRequest('get', `/deal-info/getDealActivity`, {}, { params: { dealId } })
      .then(res => {
        const activities = res?.data?.dealActivity;
        if (!Array.isArray(activities)) return;
        const match = activities.find(a => a.meetingId === meetingId) || activities[0];
        if (!match) return;
        setCallInfo({
          meetingName: (match.meetingName || '').replace(/\s*\d{10,}$/, '').trim() || 'Call',
          startTime: match.startTime,
          duration: match.totalDuration?.formatted || null,
          meetingType: match.meetingType,
          callScore: match.callScore ?? res?.data?.callScore ?? null,
        });
      })
      .catch(() => {});
  }, [dealId, meetingId]);

  // Seek audio to a timestamp (seconds), scroll transcript to matching line, start playing
  const handleTimestampSeek = (secs) => {
    if (mediaSeekRef.current) mediaSeekRef.current(secs);
    const arr = transcriptRef.current;
    if (!arr || !arr.length) return;
    let idx = arr.findIndex((line, i) => {
      const start = line.start_time ?? line.start ?? 0;
      const end = line.end_time ?? line.end ?? (arr[i + 1]?.start_time ?? arr[i + 1]?.start ?? Infinity);
      return secs >= start && secs < end;
    });
    if (idx < 0) {
      idx = arr.reduce((best, line, i) => {
        const s = line.start_time ?? line.start ?? 0;
        const bestS = arr[best]?.start_time ?? arr[best]?.start ?? 0;
        return Math.abs(s - secs) < Math.abs(bestS - secs) ? i : best;
      }, 0);
    }
    setActiveTranscriptIndex(idx);
    activeTranscriptIndexRef.current = idx;
    scrollToTranscriptLine(idx);
  };

  const scrollToTranscriptLine = (idx) => {
    const el = document.getElementById(`t-line-${idx}`);
    if (el && transcriptContainerRef.current) {
      const container = transcriptContainerRef.current;
      const elTop = el.offsetTop;
      const elH = el.offsetHeight;
      const cH = container.clientHeight;
      container.scrollTo({ top: elTop - cH / 2 + elH / 2, behavior: 'smooth' });
    }
  };

  // Sync transcript highlight with audio playback using actual start_time/end_time
  const syncTranscript = (currentTime, duration, _unused) => {
    const arr = transcriptRef.current;
    if (!arr || !arr.length || currentTime <= 0) return;

    const hasTimestamps = arr[0]?.start_time != null || arr[0]?.start != null;
    let idx;

    if (hasTimestamps) {
      idx = arr.findIndex((line, i) => {
        const start = line.start_time ?? line.start ?? 0;
        const end = line.end_time ?? line.end ?? (arr[i + 1]?.start_time ?? arr[i + 1]?.start ?? Infinity);
        return currentTime >= start && currentTime < end;
      });
      if (idx < 0) idx = arr.length - 1;
    } else {
      if (!duration) return;
      idx = Math.min(arr.length - 1, Math.floor((currentTime / duration) * arr.length));
    }

    if (idx !== activeTranscriptIndexRef.current) {
      activeTranscriptIndexRef.current = idx;
      setActiveTranscriptIndex(idx);
      scrollToTranscriptLine(idx);
    }
  };

  const TABS = [
    { id: 'feedback', label: 'AI Feedback', icon: <Brain size={13} /> },
    { id: 'strengths', label: 'Strengths', icon: <CheckCircle2 size={13} /> },
    { id: 'issues', label: 'Issues', icon: <AlertTriangle size={13} /> },
    { id: 'improvements', label: 'Improvements', icon: <TrendingUp size={13} /> },
    // { id: 'scripts', label: 'Suggested Scripts', icon: <MessageSquareQuote size={13} /> },
    { id: 'sentiment', label: 'Sentiment', icon: <Activity size={13} /> },
    { id: 'qna', label: 'Q&A', icon: <HelpCircle size={13} /> },
    { id: 'keywords', label: 'Keywords', icon: <TagIcon size={13} /> },
  ];

  return (
    <>
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        .c-fade { animation: fadeIn 0.3s ease both; }
        .c-tab:hover { background: #f3f4f6 !important; }
        .c-tab-active { background: #eff6ff !important; color: #2563eb !important; border-color: #bfdbfe !important; }
        .c-scroll::-webkit-scrollbar { width: 4px; }
        .c-scroll::-webkit-scrollbar-track { background: transparent; }
        .c-scroll::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 99px; }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#f4f6f8', fontFamily: "'Instrument Sans','Inter',sans-serif", overflow: 'hidden' }}>

        {/* ── Top Bar ── */}
        <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
          <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#fff', border: '1px solid #d1d5db', padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', color: '#374151', fontSize: '12px', fontWeight: 600 }}>
            <ArrowLeft size={13} /> Back
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Brain size={14} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>AI Coaching</div>
              {meetingId && <div style={{ fontSize: '10px', color: '#9ca3af' }}>Meeting · {meetingId}</div>}
            </div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#eff6ff', color: '#2563eb', borderRadius: '20px', padding: '3px 10px', fontSize: '10px', fontWeight: 600, border: '1px solid #bfdbfe' }}>
              <Zap size={10} fill="#2563eb" /> AI Powered
            </span>
          </div>
        </div>

        {/* ── No meetingId ── */}
        {!meetingId && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
            <div style={{ textAlign: 'center' }}>
              <Brain size={40} color="#d1d5db" style={{ marginBottom: '12px' }} />
              <p style={{ fontSize: '14px' }}>No meeting selected. Open coaching from a call activity.</p>
            </div>
          </div>
        )}

        {/* ── Two-column layout ── */}
        {meetingId && (
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden', padding: '12px', gap: '12px', flexWrap: 'wrap', minHeight: 0 }}>

            {/* ── LEFT: independent scrollable column ── */}
            <div style={{ flex: '1 1 400px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto', maxHeight: '100%' }} className="c-scroll">

              {/* Call info + Media player — fixed, never shrinks */}
              {(callInfo || audioUrl || videoUrl) && (
                <div className="c-fade" style={{
                  background: '#fff', borderRadius: '12px', overflow: 'hidden',
                  boxShadow: '0 1px 6px rgba(0,0,0,0.07)', border: '1px solid #f0f0f0',
                  flexShrink: 0
                }}>
                  {callInfo && (
                    <>
                      <div style={{ height: '3px', background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)' }} />
                      <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '32px', height: '32px', borderRadius: '8px', flexShrink: 0,
                          background: 'linear-gradient(135deg, #eff6ff, #f5f3ff)', border: '1px solid #e0e7ff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          <Phone size={14} color="#3b82f6" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: '#111827', marginBottom: '2px' }}>
                            {callInfo.meetingName || 'Call'}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                            {callInfo.startTime && (
                              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                {new Date(callInfo.startTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ', ' + new Date(callInfo.startTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                              </span>
                            )}
                            {callInfo.duration && <><span style={{ color: '#d1d5db', fontSize: '11px' }}>•</span><span style={{ fontSize: '11px', color: '#6b7280' }}>{callInfo.duration}</span></>}
                            {callInfo.meetingType && <><span style={{ color: '#d1d5db', fontSize: '11px' }}>•</span>
                              <span style={{ fontSize: '10px', fontWeight: 600, color: '#3b82f6', background: '#eff6ff', borderRadius: '4px', padding: '1px 6px', border: '1px solid #bfdbfe' }}>{callInfo.meetingType}</span>
                            </>}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {(audioUrl || videoUrl) && (
                    <div style={{ borderTop: callInfo ? '1px solid #f3f4f6' : 'none' }}>
                      <MediaPlayer audioUrl={audioUrl} videoUrl={videoUrl}
                        seekRef={mediaSeekRef}
                        onTimeUpdate={(ct, dur) => syncTranscript(ct, dur, transcript)}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Tabs — always visible once meetingId is present */}
              <div style={{ background: '#fff', borderRadius: '12px', minHeight: '320px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
                {/* Tab bar */}
                <div style={{ display: 'flex', overflowX: 'auto', borderBottom: '1px solid #f0f0f0', padding: '0 12px', gap: '2px', flexShrink: 0 }}
                  className="c-scroll">
                  {TABS.map(t => (
                    <button key={t.id} className={`c-tab${activeTab === t.id ? ' c-tab-active' : ''}`}
                      onClick={() => setActiveTab(t.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px', padding: '10px 10px',
                        border: '1px solid transparent', borderRadius: '6px', cursor: 'pointer',
                        fontSize: '11px', fontWeight: 600, color: activeTab === t.id ? '#2563eb' : '#6b7280',
                        background: 'transparent', whiteSpace: 'nowrap', flexShrink: 0, marginTop: '6px', marginBottom: '6px'
                      }}>
                      {t.icon} {t.label}
                    </button>
                  ))}
                </div>

                {/* Tab content */}
                <div className="c-scroll" style={{ overflowY: 'auto', padding: '16px', maxHeight: '400px' }}>

                  {/* Loading / error / no-data states */}
                  {loading && (
                    <div>
                      <Skeleton w="50%" mb={12} /><Skeleton mb={8} /><Skeleton w="80%" mb={8} /><Skeleton w="65%" mb={0} />
                    </div>
                  )}
                  {error && !loading && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '14px', color: '#dc2626', fontSize: '13px' }}>
                      {error}
                    </div>
                  )}
                  {!loading && !error && !data && (
                    <div style={{ textAlign: 'center', padding: '30px 0', color: '#9ca3af' }}>
                      <Brain size={32} color="#d1d5db" style={{ marginBottom: '8px' }} />
                      <p style={{ fontSize: '13px', margin: 0 }}>No coaching insights available yet.</p>
                    </div>
                  )}

                  {data && !loading && (
                    <>
                      {activeTab === 'feedback' && (
                        <div className="c-fade">
                          {data.ai_coaching_feedback?.length > 0
                            ? data.ai_coaching_feedback.map((item, i) => <FeedbackItem key={i} softIcon color="#ef4444" bg="#fef9f9" title={item.issue} reason={item.reason} time={item.mentioned_time} onSeek={handleTimestampSeek} />)
                            : <p style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No feedback available</p>}
                        </div>
                      )}
                      {activeTab === 'strengths' && (
                        <div className="c-fade">
                          {data.strengths?.length > 0
                            ? data.strengths.map((item, i) => <FeedbackItem key={i} icon="✅" color="#16a34a" bg="#f0fdf4" title={item.point} reason={item.reason} time={item.mentioned_time} onSeek={handleTimestampSeek} />)
                            : <p style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No strengths data</p>}
                        </div>
                      )}
                      {activeTab === 'issues' && (
                        <div className="c-fade">
                          {data.issues_detected?.length > 0
                            ? data.issues_detected.map((item, i) => <FeedbackItem key={i} softIcon color="#f59e0b" bg="#fffbeb" title={item.issue} reason={item.impact} time={item.mentioned_time} onSeek={handleTimestampSeek} />)
                            : <p style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No issues detected</p>}
                        </div>
                      )}
                      {activeTab === 'improvements' && (
                        <div className="c-fade">
                          {data.suggested_improvements?.length > 0
                            ? data.suggested_improvements.map((item, i) => (
                              <div key={i} style={{ display: 'flex', gap: '10px', padding: '10px 12px', background: '#eff6ff', borderRadius: '9px', marginBottom: '8px', border: '1px solid #bfdbfe' }}>
                                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#3b82f6', color: '#fff', fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>{i + 1}</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', marginBottom: '3px' }}>
                                    <p style={{ margin: 0, fontSize: '12.5px', fontWeight: 600, color: '#1e40af' }}>{item.suggestion}</p>
                                  </div>
                                  {item.expected_outcome && <p style={{ margin: 0, fontSize: '11.5px', color: '#3b82f6', lineHeight: '1.5' }}>Expected: {item.expected_outcome}</p>}
                                </div>
                              </div>
                            ))
                            : <p style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No suggestions</p>}
                        </div>
                      )}
                      {activeTab === 'scripts' && (
                        <div className="c-fade">
                          {data.script_recommendations?.length > 0
                            ? data.script_recommendations.map((item, i) => <ScriptCard key={i} instead_of={item.instead_of} try={item.try} time={item.mentioned_time} onSeek={handleTimestampSeek} />)
                            : <p style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No script recommendations</p>}
                        </div>
                      )}
                      {activeTab === 'sentiment' && (
                        <div className="c-fade">
                          {data.sentiment_timeline
                            ? <SentimentTimeline timeline={data.sentiment_timeline} onSeek={handleTimestampSeek} />
                            : <p style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No sentiment data</p>}
                        </div>
                      )}
                      {activeTab === 'qna' && (
                        <div className="c-fade">
                          {qna.length > 0
                            ? qna.map((item, i) => (
                              <QnaCard key={i} item={item} onSeek={handleTimestampSeek} />
                            ))
                            : <p style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No Q&A available</p>}
                        </div>
                      )}
                      {activeTab === 'keywords' && (
                        <div className="c-fade">
                          {keywords.length > 0
                            ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
                              {keywords.map((kw, i) => (
                                <span key={i} style={{ display: 'inline-block', padding: '4px 11px', background: `hsl(${(i * 47) % 360},70%,95%)`, color: `hsl(${(i * 47) % 360},55%,35%)`, borderRadius: '20px', fontSize: '12px', fontWeight: 600, border: `1px solid hsl(${(i * 47) % 360},60%,85%)` }}>{kw}</span>
                              ))}
                            </div>
                            : <p style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No keywords</p>}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* ── RIGHT: Transcript — independent scrollable column ── */}
            <div style={{ flex: '1 1 280px', minWidth: 0, background: '#fff', borderRadius: '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '100%', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
              {/* Header */}
              <div style={{ padding: '14px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: '#f0f9ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FileText size={15} color="#0ea5e9" />
                </div>
                <span style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>Transcript</span>
                {transcript.length > 0 && (
                  <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#6b7280', background: '#f3f4f6', borderRadius: '5px', padding: '2px 7px' }}>
                    {transcript.length} lines
                  </span>
                )}
              </div>
              {/* Scrollable transcript */}
              <div ref={transcriptContainerRef} className="c-scroll" style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
                <TranscriptPanel transcript={transcript} activeIndex={activeTranscriptIndex} transcriptRef={transcriptScrollRef} />
              </div>
            </div>

          </div>
        )}
      </div>
    </>
  );
}
